import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  CarouselButton,
  CarouselContent,
  CarouselProvider,
} from '@time-line/components/common';
import type { PromoProduct as PromoProductType } from 'hooks/cart/useCartPromos';
import { useAnalytics } from 'hooks/useAnalytics';
import type { ReactNode } from 'react';

import { PromoProduct } from '../PromoProduct';

export interface PromoCarouselProps {
  title?: ReactNode;
  products: PromoProductType[];
  disabled?: boolean;
  progress: number;
  renderProduct?: (
    product: PromoProductType,
    disabled?: boolean,
    onAccept?: () => void,
  ) => ReactNode;
}

export function PromoCarousel({
  title,
  products,
  progress,
  renderProduct,
  ...props
}: PromoCarouselProps) {
  const { analytics } = useAnalytics();

  const clampedProgress = Math.max(0, Math.min(1, progress ?? 0));
  const progressWidth = `${clampedProgress * 100}%`;

  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' },
  );

  return (
    <CarouselProvider>
      <Box mt="2xs" mb="2xs" mx={{ base: '-sm', md: '-xl' }}>
        <Box mx={{ base: 'sm', md: 'xl' }}>
          <Box
            mb="2xs"
            position="relative"
            height="5xs"
            width="100%"
            bgColor="blackAlpha.250"
            borderRadius="full"
          >
            <Box
              position="absolute"
              left={0}
              top={0}
              height="100%"
              width={progressWidth}
              bgColor="gray.600"
              transitionProperty="width"
              transitionDuration="normal"
              borderRadius="full"
            />
          </Box>
          <Flex alignItems="center" justifyContent="space-between" mb="2xs">
            {title && <Text size="overline">{title}</Text>}
            <Flex gap="3xs">
              <CarouselButton
                direction="prev"
                _disabled={{
                  pointerEvents: 'none',
                  transitionDuration: 'slow',
                  color: 'blackAlpha.500',
                }}
              />
              <CarouselButton
                direction="next"
                _disabled={{
                  pointerEvents: 'none',
                  transitionDuration: 'slow',
                  color: 'blackAlpha.500',
                }}
              />
            </Flex>
          </Flex>
        </Box>
        <Box mx={{ base: '0', md: 'xl' }}>
          <CarouselContent
            breakpoints={{
              0: {
                slidesPerView: 1.15,
                spaceBetween: 12,
              },
              768: {
                slidesPerView: 1,
                spaceBetween: 24,
              },
              960: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
            }}
            slidesPerView={1}
            centeredSlides={isMobile}
            loop={false}
          >
            {products.map((product) => {
              const onAccept = () =>
                analytics.freegiftAccept({ product: product.slug });

              return (
                <Box
                  key={product.slug}
                  onClick={(e) => {
                    if (!renderProduct) {
                      const target = e.target as HTMLElement | null;
                      if (target && target.closest('.button--add-to-cart')) {
                        onAccept();
                      }
                    }
                  }}
                >
                  {renderProduct ? (
                    renderProduct(product, props.disabled, onAccept)
                  ) : (
                    <PromoProduct {...product} {...props} />
                  )}
                </Box>
              );
            })}
          </CarouselContent>
        </Box>
      </Box>
    </CarouselProvider>
  );
}
